"use client";
import Image from "next/image";
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { useAppContext } from "@/context/AppContext";
import styles from "./Modal.module.scss";
import dynamic from "next/dynamic";
import { CustomLink } from "@/components/CustomLink";
import { Modal as ModalPrimitive } from "@/components/_primitives/Modal/Modal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPrevButton,
} from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import { HeaderVideosType } from "@/types/PageType";
import { BoxLoader } from "@/components/Loader/Loader";
import { ModalAnimatedContent } from "@/components/_primitives/Modal/ModalAnimatedContent/ModalAnimatedContent";

const VidstackPlayer = dynamic(
  () => import("../../../components/VidstackPlayer/VidstackPlayer"),
  { ssr: false },
);

/* -------------------------------------------------------------------------- */
/*  Module-level components: stable identity => no remount when Modal renders  */
/* -------------------------------------------------------------------------- */

type InstaVideoProps = {
  video: {
    media_url: string;
    thumbnail_url: string | null;
    alt_text: string | null;
    caption?: string | null;
  };
  i?: number;
  open: boolean;
  onLoaded: () => void;
};

const InstaVideo = memo(function InstaVideo({
  video,
  i,
  open,
  onLoaded,
}: InstaVideoProps) {
  // always read the latest `open` without changing the onLoad callback identity
  const openRef = useRef(open);
  openRef.current = open;

  const handleLoad = useCallback(() => {
    if (!openRef.current) return;
    if (i && i !== 0) return;
    onLoaded();
  }, [i, onLoaded]);

  const { media_url, thumbnail_url, caption } = video;

  const videoData = useMemo<HeaderVideosType>(
    () => ({
      copyright: "", // hotfix, pls set correctly
      audio: true,
      audio_description_video_desktop: false,
      captions: false,
      description: caption ?? "",
      transcript: { textfile: false, text: "" },
      contains_fast_moves: false,
      contains_flashing_content: false,
      contains_problematic_content: false,
      internes_video_desktop: {
        url: media_url,
        alt: "",
        title: "",
        author: "",
        description: caption ?? "",
        filename: "",
        id: "",
        link: "",
      },
      video_thumbnail_desktop: {
        url: thumbnail_url ?? "",
        width: 0,
        height: 0,
        alt: caption ?? "",
        type: "image",
        author: "",
        caption: caption ?? "",
        date: "",
        description: "",
        filename: "",
        id: "",
        link: "",
        title: "",
        image_blur: "",
        mime_type: "image/webp",
        mimetype: "image/webp",
        modified: "",
        name: "",
        acf: { link_1: "", link_2: "", text_1: "", text_2: "" },
      },
      video_type: "upload",
      externes_video: null,
      extern_video_audio_description: null,
      externes_video_iframe: "",
      internes_video_tablet: false,
      externes_video_iframe_audio_description: "",
      has_single_page: false,
      internes_video_fullhd: false,
      internes_video_mobile: false,
      video_thumb_fullhd: false,
      video_thumb_tablet: false,
      video_thumb_mobile: false,
      video_thumb_extern: false,
      seo_settings: {
        breadcrumb_title: "",
        seo_title: "",
        seo_metadescription: "",
        social_media_share_description: "",
        exclude_from_search_engines: false,
        social_media_share_image: false,
        show_in_sitemap: false,
      },
    }),
    [media_url, thumbnail_url, caption],
  );

  return (
    <VidstackPlayer
      onLoad={handleLoad}
      autoplay={false}
      loop={true}
      muted={false}
      video_decorative={true}
      video={videoData}
    />
  );
});

type InstaImageProps = {
  image: {
    permalink: string;
    media_url: string;
    alt_text: string | null;
  };
  i?: number;
  open: boolean;
  loaded: boolean;
  ariaLabel: string;
  onLoaded: () => void;
};

const InstaImage = memo(function InstaImage({
  image,
  i,
  open,
  loaded,
  ariaLabel,
  onLoaded,
}: InstaImageProps) {
  return (
    <CustomLink
      href={image.permalink}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={ariaLabel}
      className={styles["post-image-link"]}
    >
      <Image
        ref={(img) => {
          if (img && img.complete && img.naturalWidth > 0) onLoaded();
        }}
        className={styles["post-image"]}
        data-is-loading={!loaded}
        src={image.media_url}
        alt={image.alt_text ?? ""}
        height={1200}
        width={1200}
        onLoad={() => {
          if (!open) return;
          if (i && i !== 0) return;
          onLoaded();
        }}
        sizes="(max-width: 767px) 80vw,(max-width: 1215px) 75vw, 50vw"
        quality={90}
      />
    </CustomLink>
  );
});

export type InstagramModalType = {
  media_type: "carousel_album" | "video" | "image";
  media_url: string;
  thumbnail_url: string | null;
  alt_text: string | null;
  caption: string | null;
  children:
    | {
        media_type: "video" | "image";
        media_url: string;
        thumbnail_url: string | null;
        alt_text: string | null;
      }[]
    | null;
  permalink: string;
};

/** Context state: the opened post + whether the modal animates */
export type InstagramModalState = {
  post: InstagramModalType;
  animated: boolean;
};

export const InstagramModal = () => {
  const { instagramPostModal, setInstagramPostModal } = useAppContext();

  return instagramPostModal ? (
    <InstagramPostModal
      key={instagramPostModal.post.permalink}
      post={instagramPostModal.post}
      animated={instagramPostModal.animated}
      onExited={() => {
        setInstagramPostModal((current) =>
          current === instagramPostModal ? null : current,
        );
      }}
    />
  ) : null;
};

interface InstagramPostModalProps {
  post: InstagramModalType;
  animated: boolean;
  onExited: () => void;
}

const InstagramPostModal = ({
  post,
  animated,
  onExited,
}: InstagramPostModalProps) => {
  const carouselId = useId();
  const { getLabel } = useGlobalContext();

  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(true);

  // stable identity so memoized children don't re-render for no reason
  const handleLoaded = useCallback(() => setLoaded(true), []);

  const showLoader = useDelayedFlag(open && !loaded, 1500);

  const caption = post.caption ?? "";

  const imageAriaLabel = `${getLabel("open_project", "Öffne")} ${caption} in Instagram`;

  return (
    <ModalPrimitive
      animation={animated ? "staggered" : "none"}
      contentReady={loaded}
      open={open}
      setOpen={setOpen}
      onMountedChange={(mounted) => {
        if (!mounted) {
          onExited();
        }
      }}
      ariaLabel={post.caption || "Instagram"}
    >
      <>
        <Carousel>
          <ModalAnimatedContent>
            <div className={styles["insta-modal"]}>
              {post.media_type === "video" ? (
                <InstaVideo video={post} open={open} onLoaded={handleLoaded} />
              ) : post.media_type === "carousel_album" ? (
                <CarouselContent
                  spacing="16px"
                  extraClasses={`${styles["carousel-content"]} ${carouselId}`}
                  onClick={() => setOpen(false)}
                >
                  {post.children?.map((child, i) => (
                    <CarouselItem
                      key={i}
                      className="is-flex is-align-items-center is-justify-content-center"
                      itemWidth={"100%"} // delete for DynamicCarousel
                    >
                      {child.media_type === "image" ? (
                        <InstaImage
                          image={{ ...child, permalink: post.permalink }}
                          i={i}
                          open={open}
                          loaded={loaded}
                          ariaLabel={imageAriaLabel}
                          onLoaded={handleLoaded}
                        />
                      ) : (
                        <InstaVideo
                          video={child}
                          i={i}
                          open={open}
                          onLoaded={handleLoaded}
                        />
                      )}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              ) : (
                <InstaImage
                  image={post}
                  open={open}
                  loaded={loaded}
                  ariaLabel={imageAriaLabel}
                  onLoaded={handleLoaded}
                />
              )}
            </div>
          </ModalAnimatedContent>

          {post.media_type === "carousel_album" && loaded && open && (
            <div className={styles["carousel-nav-wrapper"]}>
              <CarouselPrevButton
                className={styles["prev-btn"]}
                iconLabel="bare_arrow_right"
              />
              <CarouselNextButton
                className={styles["next-btn"]}
                iconLabel="bare_arrow_right"
              />
            </div>
          )}
        </Carousel>

        {showLoader && <BoxLoader className={styles["loading-animation"]} />}
      </>
    </ModalPrimitive>
  );
};

function useDelayedFlag(active: boolean, delay = 200) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!active) {
      setShow(false);
      return;
    }
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [active, delay]);
  return show;
}

// The following might be useful if projects wants the modal carousel
// to only be as width as its content (to prevent moving through the whole viewport on swipe)
// and we have different content widths in one carousel

/*
const DynamicCarousel = () => {
  const { emblaApi } = useCarousel();

  useEffect(() => {
    if (!emblaApi) return;
    const root = emblaApi.rootNode();
    root.style.transition = "width 0.3s ease-in-out";
    const fit = () => {
      const slide = emblaApi.slideNodes()[emblaApi.selectedScrollSnap()];
      if (!slide) return;
      // spacing read from the slide instead of hardcoded
      const gap = parseFloat(getComputedStyle(slide).paddingLeft) || 0;
      root.style.width = `${slide.offsetWidth - gap}px`;
    };

    let raf = 0;
    const remeasure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => emblaApi.reInit()); // fires "reInit" -> fit
    };

    const ro = new ResizeObserver(remeasure);
    emblaApi.slideNodes().forEach((s) => ro.observe(s));

    emblaApi.on("reInit", fit).on("select", fit);
    fit(); // "init" already happened

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      emblaApi.off("reInit", fit).off("select", fit);
    };
  }, [emblaApi]);

  return null;
};
*/
